import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, MapPin, Calendar, Award, Leaf } from 'lucide-react';
import { getImageUrl } from '@/utils/helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FarmDetailPage = () => {
  const { farmId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [farm, setFarm] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFarm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farmId]);

  const fetchFarm = async () => {
    try {
      const res = await axios.get(`${API}/farms/${farmId}`);
      setFarm(res.data);
    } catch (err) {
      console.error('Failed to fetch farm', err);
    } finally {
      setLoading(false);
    }
  };

  const getLocalizedField = (item, field) => {
    if (!item) return '';
    const lang = i18n.language;
    if (lang === 'en' && item[`${field}_en`]) return item[`${field}_en`];
    if (lang === 'ru' && item[`${field}_ru`]) return item[`${field}_ru`];
    return item[field] || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!farm) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-amber-800 mb-4">{t('common.notFound', '未找到')}</h2>
          <Button onClick={() => navigate(-1)} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" /> {t('common.back', '返回')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-amber-50" data-testid="farm-detail-page">
      {/* Hero */}
      <div className="relative h-[50vh] bg-gray-900">
        <img loading="lazy"
          src={getImageUrl(farm.image_url)} 
          alt={farm.name} 
          className="w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="container mx-auto">
            <Button variant="ghost" className="text-white mb-4" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" /> {t('common.back', '返回')}
            </Button>
            <div className="flex flex-wrap gap-2 mb-4">
              {farm.certifications?.map((cert, idx) => (
                <Badge key={idx} className="bg-green-600 text-white">
                  <Award className="w-3 h-3 mr-1" /> {cert}
                </Badge>
              ))}
            </div>
            <h1 className="text-4xl font-bold text-white mb-2">
              {getLocalizedField(farm, 'name')}
            </h1>
            <div className="flex items-center gap-4 text-white/80">
              {farm.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" /> {getLocalizedField(farm, 'location')}
                </span>
              )}
              {farm.altitude && (
                <span className="flex items-center gap-1">
                  🏔️ {t('farm.altitude', '海拔')} {farm.altitude}
                </span>
              )}
              {farm.founded_year && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" /> {t('farm.founded', '成立于')} {farm.founded_year}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Leaf className="w-5 h-5 text-green-600" />
                  {t('farm.about', '关于农场')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-amber-800 leading-relaxed">
                  {getLocalizedField(farm, 'description')}
                </p>
              </CardContent>
            </Card>

            {/* Story */}
            {farm.story && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('farm.story', '农场故事')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-amber-800 leading-relaxed whitespace-pre-line">
                    {getLocalizedField(farm, 'story')}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Gallery */}
            {farm.images?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('farm.gallery', '农场相册')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {farm.images.map((img, idx) => (
                      <div key={idx} className="aspect-square rounded-lg overflow-hidden">
                        <img src={img} alt={`${farm.name} ${idx + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Products */}
            {farm.products?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('farm.products', '主营产品')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {farm.products.map((product, idx) => (
                      <Badge key={idx} variant="secondary" className="text-sm py-1.5 px-3">
                        {product}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contact */}
            {farm.contact && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('farm.contact', '联系方式')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-amber-800">{farm.contact}</p>
                </CardContent>
              </Card>
            )}

            {/* Video */}
            {farm.video_url && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('farm.video', '视频介绍')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <video 
                    src={farm.video_url} 
                    controls 
                    className="w-full rounded-lg"
                    poster={farm.image_url}
                  />
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FarmDetailPage;
